import seedrandom from 'seedrandom';

export const moneyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * Formats simulation time in seconds to a human-readable MM:SS format
 * @param seconds - Time in seconds (can be fractional)
 * @returns Formatted time string like "05:30" or "120:45" for longer durations
 */
export function formatSimulationTime(seconds: number): string {
  const totalSeconds = Math.floor(Math.abs(seconds)); // Handle negative values gracefully
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Formats simulation time in seconds to a detailed human-readable format
 * @param seconds - Time in seconds (can be fractional)
 * @returns Formatted time string like "5m 30s" or "1h 5m 30s" for longer durations
 */
export function formatSimulationTimeDetailed(seconds: number): string {
  const totalSeconds = Math.floor(Math.abs(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds}s`);
  }

  return parts.join(' ');
}

/**
 * Formats simulation time with appropriate precision based on duration
 * @param seconds - Time in seconds
 * @param showSeconds - Whether to show seconds in the output (default: true for short durations)
 * @returns Formatted time string
 */
export function formatSimulationTimeAdaptive(seconds: number, showSeconds: boolean = true): string {
  const totalSeconds = Math.floor(Math.abs(seconds));

  // For durations over 10 minutes, default to not showing seconds unless explicitly requested
  if (totalSeconds >= 600 && showSeconds === true) {
    showSeconds = false;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    const minutesPart = minutes.toString().padStart(2, '0');
    if (showSeconds) {
      const secondsPart = remainingSeconds.toString().padStart(2, '0');
      return `${hours}:${minutesPart}:${secondsPart}`;
    }
    return `${hours}:${minutesPart}`;
  }

  if (showSeconds) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}m`;
}

export class RandomNumberGenerator {
  private readonly rng: seedrandom.PRNG;

  constructor(seed: string) {
    this.rng = seedrandom(seed);
  }

  number(min: number, max: number): number {
    return Math.floor(this.rng() * (max - min + 1)) + min;
  }

  element(elements: string[], excludeElements: string[] = []): string {
    const filteredElements = elements.filter((el) => excludeElements.indexOf(el) === -1);
    const randomIndex = Math.floor(this.rng() * filteredElements.length);
    return filteredElements[randomIndex];
  }
}
